/**
 * This is a patch of the `useResizeObserver` function from vueuse.
 * For more details, see comments in ./use-element-bounding-patch.ts
 */
import { type MaybeComputedElementRef, defaultWindow, tryOnScopeDispose, unrefElement, useSupported, type UseResizeObserverOptions, type UseResizeObserverReturn } from '@vueuse/core'
import { watch } from 'vue-demi'

/**
 * Reports changes to the dimensions of an Element's content or the border-box
 *
 * @see https://vueuse.org/useResizeObserver
 * @param target
 * @param callback
 * @param options
 */
export function useSingleElementResizeObserver (
  target: MaybeComputedElementRef,
  callback: ResizeObserverCallback,
  options: UseResizeObserverOptions = {}
): UseResizeObserverReturn {
  const { window = defaultWindow, ...observerOptions } = options
  let observer: ResizeObserver | undefined
  const isSupported = useSupported(() => (window != null) && 'ResizeObserver' in window)

  const cleanup = () => {
    if (observer != null) {
      observer.disconnect()
      observer = undefined
    }
  }

  const stopWatch = watch(
    () => unrefElement(target),
    (el) => {
      cleanup()
      if (isSupported.value && (window != null)) {
        observer = new ResizeObserver(callback)
        el != null && observer.observe(el, observerOptions)
      }
    },

    { immediate: true, flush: 'post' /** !! Removed `deep: true` !! */ }
  )

  const stop = () => {
    cleanup()
    stopWatch()
  }

  tryOnScopeDispose(stop)

  return {
    isSupported,
    stop
  }
}

export type UseSingleElementResizeObserverReturn = UseResizeObserverReturn
